import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Typography,
  Select,
  MenuItem,
  Stack,
  Button,
  FormControl,
  InputLabel,
} from "@mui/material";

import VisibilityIcon from "@mui/icons-material/Visibility";

import { DataGrid } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid";

import { getExpedientesPorMes,marcarRevisado } from "../services/expedientes3.service";
import ExpedienteDetalleModal from "../expedientes/ExpedienteDetalleModal3";
/* =========================
   TYPES
========================= */

export interface Expediente {
  id: number;
  nro_expediente: string;
  tipo_expediente: string;
  estado: string;
  tipo_estado: string;
  organo_jurisdiccional: string;
  demandante: string;
  demandados: string | null;
  partes_procesales: string;
  fecha_ingreso: string;
  materia: string;
  observaciones: string | null;

  telefono?: string | null;
  telefono_doxeado?: string | null;
  revisado?: boolean;
}



/* =========================
   CONSTANTS
========================= */

const currentYear = new Date().getFullYear();

const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i);

const MESES = [
  { value: 1, label: "Enero" },
  { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" },
  { value: 6, label: "Junio" },
  { value: 7, label: "Julio" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" },
  { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" },
  { value: 12, label: "Diciembre" },
];

/* =========================
   COMPONENT
========================= */

const DashboardPage = () => {
  const navigate = useNavigate();
  const now = new Date();

  const [anio, setAnio] = useState(now.getFullYear());
  const [mes, setMes] = useState(now.getMonth() + 1);

  const [rows, setRows] = useState<Expediente[]>([]);
  const [loading, setLoading] = useState(false);

  const [materiaFiltro, setMateriaFiltro] = useState<string>("TODAS");

  const [openDetalle, setOpenDetalle] = useState(false);
  const [selectedExpediente, setSelectedExpediente] =
    useState<Expediente | null>(null);

  /* =========================
     LOGIN GUARD
  ========================== */

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) navigate("/login");
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  /* =========================
     FETCH DATA
  ========================== */

  const fetchData = async () => {
  setLoading(true);

  try {
    const resp = await getExpedientesPorMes(anio, mes);

    const materiasPermitidas = [
      "EJECUCION DE GARANTIAS",
      "OBLIGACION DE DAR SUMA DE DINERO",
    ];

    const filtrados = resp.filter((exp: Expediente) =>
      materiasPermitidas.includes(exp.materia)
    );

    setRows(filtrados);
  } catch (err) {
    console.error("Error cargando expedientes:", err);
    setRows([]);
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  fetchData();
}, [anio, mes]);

  /* =========================
     MATERIAS DINÁMICAS
  ========================== */

  const materiasDisponibles = Array.from(
    new Set(rows.map((r) => r.materia))
  );

  const rowsFiltrados =
    materiaFiltro === "TODAS"
      ? rows
      : rows.filter((r) => r.materia === materiaFiltro);

  /* =========================
     ACCIONES
  ========================== */

  const handleVerDetalle = (exp: Expediente) => {
    setSelectedExpediente(exp);
    setOpenDetalle(true);
  };

  const marcarRevisadoHandler = async (id: number) => {
  try {
    await marcarRevisado(id);

    setRows((prev) =>
      prev.map((r) =>
        r.id === id ? { ...r, revisado: true } : r
      )
    );
  } catch (err) {
    console.error("Error marcando revisado", err);
  }
};

  /* =========================
     COLUMNS
  ========================== */

  const columns: GridColDef[] = [
    { field: "nro_expediente", headerName: "Expediente", flex: 1.5 },
    { field: "organo_jurisdiccional", headerName: "Órgano", flex: 1.6 },
    { field: "tipo_expediente", headerName: "Tipo", flex: 1.1 },
    { field: "materia", headerName: "Materia", flex: 1 },
    { field: "estado", headerName: "Estado / Contacto", flex: 1.4 },
    {
      field: "revisado",
      headerName: "Revisado",
      width: 120,
      renderCell: (params) => (params.row.revisado ? "✅" : "❌"),
    },
    {
      field: "accion_revisado",
      headerName: "Acción",
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          color={params.row.revisado ? "success" : "primary"}
onClick={() => marcarRevisadoHandler(params.row.id)}
        >
          {params.row.revisado ? "OK" : "Revisar"}
        </Button>
      ),
    },
    {
  field: "telefono_doxeado",
  headerName: "Teléfono 2",
  flex: 1.2,
  renderCell: (params) =>
    params.row.telefono_doxeado ? (
      <span>📱 {params.row.telefono_doxeado}</span>
    ) : (
      "—"
    ),
},
    {
      field: "acciones",
      headerName: "",
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <VisibilityIcon
          sx={{ cursor: "pointer" }}
          onClick={() => handleVerDetalle(params.row)}
        />
      ),
    },
  ];

  /* =========================
     RENDER
  ========================== */

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Typography variant="h6" fontWeight={600}>
          Casos recientes
        </Typography>

        <Stack direction="row" spacing={2}>
          <FormControl size="small">
            <InputLabel>Año</InputLabel>
            <Select
              value={anio}
              label="Año"
              onChange={(e) => setAnio(Number(e.target.value))}
            >
              {YEARS.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small">
            <InputLabel>Mes</InputLabel>
            <Select
              value={mes}
              label="Mes"
              onChange={(e) => setMes(Number(e.target.value))}
            >
              {MESES.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 🔥 NUEVO FILTRO DINÁMICO */}
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Materia</InputLabel>
            <Select
              value={materiaFiltro}
              label="Materia"
              onChange={(e) => setMateriaFiltro(e.target.value)}
            >
              <MenuItem value="TODAS">Todas</MenuItem>

              {materiasDisponibles.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>



          <Button variant="contained" color="error" onClick={handleLogout}>
            Logout
          </Button>
        </Stack>
      </Stack>

      <DataGrid
        rows={rowsFiltrados}
        columns={columns}
        loading={loading}
        autoHeight
        pageSizeOptions={[5, 10, 20]}
        disableRowSelectionOnClick
        getRowId={(row) => row.id}
        localeText={{ noRowsLabel: "No hay expedientes" }}
      />

      <ExpedienteDetalleModal
  open={openDetalle}
  expediente={selectedExpediente}
  onClose={() => setOpenDetalle(false)}
  onTelefonoGuardado={fetchData}
/>
    </Box>
  );
};

export default DashboardPage;